import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { contactInfo, pick, scrollToId } from './landingUtils';

// Jumps to a landing section: in place on the home page, otherwise navigates
// home and lets LandingPage scroll there (location.state.scrollTo).
export const useGoToSection = () => {
  const location = useLocation();
  const navigate = useNavigate();
  return (id) => {
    if (location.pathname === '/') scrollToId(id);
    else navigate('/', { state: { scrollTo: id } });
  };
};

// The brand mark drawn as two diamonds and the gold point.
export const BrandMark = ({ className = '', tone = 'dark', width = 44, stroke = 5, ...rest }) => {
  const c = tone === 'light'
    ? { l: '#B8932A', r: '#0A0A0A', d: '#B8932A' }
    : { l: '#D4AF37', r: '#F4F1EA', d: '#F4CF67' };
  return (
    <svg className={className} width={width} viewBox="-3 -3 106 56" aria-hidden="true" style={{ overflow: 'visible', flex: 'none' }} {...rest}>
      <polygon className="ml" points="0,25 25,0 50,25 25,50" fill="none" stroke={c.l} strokeWidth={stroke} />
      <polygon className="mr" points="50,25 75,0 100,25 75,50" fill="none" stroke={c.r} strokeWidth={stroke} />
      <circle className="md" cx="50" cy="25" r={stroke + 1} fill={c.d} />
    </svg>
  );
};

// Heavy uppercase heading with one word set in the italic gold serif.
// Three or more words: the second word; otherwise the last one.
export const AccentTitle = ({ text, as: Tag = 'h2', className = 'h-l' }) => {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length < 2) return <Tag className={className}>{text}</Tag>;
  const at = words.length >= 3 ? 1 : words.length - 1;
  return (
    <Tag className={className}>
      {words.map((w, i) => (
        <React.Fragment key={i}>
          {i > 0 && ' '}
          {i === at ? <span className="voice">{w.toLowerCase()}</span> : w}
        </React.Fragment>
      ))}
    </Tag>
  );
};

const Eyebrow = ({ children }) => <span className="eyebrow">{children}</span>;
export { Eyebrow };

const IconPhone = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2" /></svg>
);
const IconChat = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 21l1.7-5A8.5 8.5 0 1112 20.5a8.4 8.4 0 01-4-1z" /></svg>
);

export function LandingNav({ settings, lang, menuOpen, setMenuOpen }) {
  const [solid, setSolid] = useState(false);
  const progressRef = useRef(null);
  const info = contactInfo(settings);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setSolid(y > 40);
      if (progressRef.current) progressRef.current.style.transform = `scaleX(${max > 0 ? y / max : 0})`;
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const bs = lang === 'bs';
  const items = [
    { id: 'services', label: bs ? 'Usluge' : 'Services', show: settings?.showServices !== false },
    { to: '/projekti', label: bs ? 'Projekti' : 'Projects', show: true },
    { id: 'gallery', label: bs ? 'Galerija' : 'Gallery', show: settings?.showGallery !== false, mobileOnly: true },
    { id: 'packages', label: bs ? 'Paketi' : 'Packages', show: settings?.showPackages !== false },
    { id: 'galerija-gostiju', label: bs ? 'QR galerija' : 'QR gallery', show: settings?.showGuestGallery !== false },
    { id: 'faq', label: 'FAQ', show: settings?.showFAQ !== false },
    { to: '/za-kolege', label: bs ? 'Za kolege' : 'For colleagues', show: true },
    { id: 'contact', label: bs ? 'Kontakt' : 'Contact', show: true, mobileOnly: true },
  ].filter((i) => i.show);

  const goTo = useGoToSection();
  const go = (id) => { setMenuOpen(false); goTo(id); };

  return (
    <>
      <header className={`nav${solid ? ' solid' : ''}`}>
        <div className="wrap row">
          <button className="brand" onClick={() => go('home')} aria-label="Infinity Creative Agency">
            <BrandMark width={44} />
            <b>{(settings?.siteName ? settings.siteName.split(' ')[0] : 'Infinity').toUpperCase()}</b>
          </button>
          <nav className="links" aria-label={bs ? 'Glavna navigacija' : 'Main navigation'}>
            {items.filter((i) => !i.mobileOnly).map((i) => (i.to
              ? <Link key={i.label} to={i.to}>{i.label}</Link>
              : <button key={i.label} onClick={() => go(i.id)}>{i.label}</button>))}
          </nav>
          <button className="btn btn-gold btn-sm" onClick={() => go('contact')}><span className="d" />{bs ? 'Rezerviši datum' : 'Book a date'}</button>
          <button
            className="burger"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? (bs ? 'Zatvori meni' : 'Close menu') : (bs ? 'Otvori meni' : 'Open menu')}
            aria-expanded={menuOpen}
          ><i /><i /></button>
        </div>
        <div className="progress" ref={progressRef} />
      </header>

      <div className="menu" aria-hidden={!menuOpen}>
        {items.map((i) => (i.to
          ? <Link key={i.label} className="item" to={i.to} onClick={() => setMenuOpen(false)} tabIndex={menuOpen ? 0 : -1}>{i.label}</Link>
          : <button key={i.label} className="item" onClick={() => go(i.id)} tabIndex={menuOpen ? 0 : -1}>{i.label}</button>))}
        <div className="menu-foot">
          <a href={`tel:${info.phone}`} tabIndex={menuOpen ? 0 : -1}>{info.phoneRaw}</a>
          <a href={`mailto:${info.email}`} tabIndex={menuOpen ? 0 : -1}>{info.email}</a>
          <div className="social">
            {info.instagram && <a href={info.instagram} target="_blank" rel="noopener noreferrer" tabIndex={menuOpen ? 0 : -1}>Instagram</a>}
            {info.facebook && <a href={info.facebook} target="_blank" rel="noopener noreferrer" tabIndex={menuOpen ? 0 : -1}>Facebook</a>}
          </div>
        </div>
      </div>
    </>
  );
}

// Phone-only bottom bar: call, WhatsApp and the booking button.
export function MobileDock({ settings, lang }) {
  const [show, setShow] = useState(false);
  const info = contactInfo(settings);
  const goTo = useGoToSection();
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > window.innerHeight * 0.7);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div className={`dock${show ? ' show' : ''}`}>
      <div className="icons">
        <a href={`tel:${info.phone}`} aria-label={lang === 'bs' ? 'Pozovi' : 'Call'}><IconPhone /></a>
        <a href={info.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp"><IconChat /></a>
      </div>
      <button className="btn btn-gold" onClick={() => goTo('contact')}><span className="d" />{lang === 'bs' ? 'Rezerviši datum' : 'Book a date'}</button>
    </div>
  );
}

// Diamond cursor with a trailing frame; only for a real mouse.
export function DiamondCursor() {
  const frame = useRef(null);
  const dot = useRef(null);
  useEffect(() => {
    if (!window.matchMedia('(pointer: fine)').matches || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let mx = -100, my = -100, cx = -100, cy = -100, raf;
    const move = (e) => {
      mx = e.clientX; my = e.clientY;
      if (dot.current) dot.current.style.transform = `translate(${mx}px, ${my}px)`;
      const hot = e.target.closest && e.target.closest('a, button, .shot, .film');
      if (frame.current) frame.current.classList.toggle('big', !!hot);
    };
    const loop = () => {
      cx += (mx - cx) * 0.16; cy += (my - cy) * 0.16;
      if (frame.current) frame.current.style.transform = `translate(${cx}px, ${cy}px) rotate(45deg)`;
      raf = requestAnimationFrame(loop);
    };
    window.addEventListener('mousemove', move);
    raf = requestAnimationFrame(loop);
    return () => { window.removeEventListener('mousemove', move); cancelAnimationFrame(raf); };
  }, []);
  return (<><div className="cursor" ref={frame} aria-hidden="true" /><div className="cdot" ref={dot} aria-hidden="true" /></>);
}

export function LandingFooter({ settings, lang }) {
  const bs = lang === 'bs';
  const info = contactInfo(settings);
  const goTo = useGoToSection();
  const about = pick(settings, 'footerAbout', lang, [
    'Pretvaramo vaše vizije u stvarnost kroz vrhunsku produkciju i kreativna digitalna rješenja.',
    'We turn your visions into reality through top production and creative digital solutions.',
  ]);
  const copyright = pick(settings, 'footerCopyright', lang, [
    `© ${new Date().getFullYear()} Infinity Creative Agency. Sva prava pridržana.`,
    `© ${new Date().getFullYear()} Infinity Creative Agency. All rights reserved.`,
  ]);
  const word = (settings?.siteName ? settings.siteName.split(' ')[0] : 'Infinity').toUpperCase();
  return (
    <footer id="site-footer">
      <div className="wrap">
        <div className="foot">
          <div>
            <button className="brand" onClick={() => goTo('home')} style={{ lineHeight: 1 }}>
              <BrandMark width={44} /><b style={{ color: 'var(--ivory)' }}>{word}</b>
            </button>
            <p style={{ marginTop: 18, maxWidth: '34ch', lineHeight: 1.7 }}>{about}</p>
          </div>
          <div>
            <h4>{bs ? 'Stranica' : 'Site'}</h4>
            {settings?.showServices !== false && <button onClick={() => goTo('services')}>{bs ? 'Usluge' : 'Services'}</button>}
            {settings?.showPackages !== false && <button onClick={() => goTo('packages')}>{bs ? 'Paketi' : 'Packages'}</button>}
            {settings?.showFAQ !== false && <button onClick={() => goTo('faq')}>FAQ</button>}
            <button onClick={() => goTo('contact')}>{bs ? 'Kontakt' : 'Contact'}</button>
          </div>
          <div>
            <h4>{bs ? 'Agencija' : 'Agency'}</h4>
            <Link to="/projekti">{bs ? 'Projekti' : 'Projects'}</Link>
            <Link to="/films">{bs ? 'Filmovi' : 'Films'}</Link>
            <Link to="/qr-galerija">{bs ? 'QR galerija' : 'QR gallery'}</Link>
            <Link to="/za-kolege">{bs ? 'Za kolege' : 'For colleagues'}</Link>
          </div>
          <div>
            <h4>{bs ? 'Kontakt' : 'Contact'}</h4>
            <a href={`tel:${info.phone}`}>{info.phoneRaw}</a>
            <a href={`mailto:${info.email}`}>{info.email}</a>
            {info.instagram && <a href={info.instagram} target="_blank" rel="noopener noreferrer">Instagram</a>}
            {info.facebook && <a href={info.facebook} target="_blank" rel="noopener noreferrer">Facebook</a>}
          </div>
        </div>
        <div className="giant" aria-hidden="true">{word.split('').map((ch, i) => <span key={i}>{ch}</span>)}</div>
        <div className="legal"><span>{copyright}</span><span>{pick(settings, 'heroSubtitle', lang, ['Vaša priča, naš objektiv', 'Your story, our lens'])}</span></div>
      </div>
    </footer>
  );
}
